<script setup>
import { ref, watch, computed, defineAsyncComponent } from 'vue'
import { useChannelsStore } from '@/stores/channels'
import { useSignature } from '@/stores/signatures'

const signatureStore = useSignature()

import InputFormHorizontal from '@/components/UI/Form/InputFormHorizontal.vue'
import SelectContactCountryCode from '@/components/UI/Form/SelectContactCountryCode.vue'
import FeedbackAlert from '@/components/utils/FeedbackAlert.vue'
const TelephonyOmniChannelUsersDepartaments = defineAsyncComponent(() => import('@/views/dash/channels/TelephonyOmniChannelUsersDepartaments.vue'))

const emit = defineEmits(['update:acceptTerms'])

const TabUserDepartment = defineAsyncComponent(() => import('@/components/tabs/TabUserDepartment.vue'))
const ChatTransition = defineAsyncComponent(() => import('@/components/utils/ChatTransition.vue'))
const Toggle = defineAsyncComponent(() => import('@/components/UI/Toogle/ToogleWithDescription.vue'))

import AlertPrice from '@/views/dash/channels/Pages/AlertPrice.vue'
import Checkbox from 'primevue/checkbox'
import TabView from 'primevue/tabview'
import TabPanel from 'primevue/tabpanel'
const store = useChannelsStore()

const authorizationAttendance = ref('')

store.channel.type = store.channel.type || 'whatsapp'
store.channel.server_type = store.channel.server_type || 'goMD'

const toBool = value => value === true || value === 'true'

const prevMore = store.channel.more && typeof store.channel.more === 'object' ? { ...store.channel.more } : {}
store.channel.more = {
    ...prevMore,
    reject_calls: toBool(prevMore.reject_calls),
    file_name: toBool(prevMore.file_name),
    telephony_omnichannel: toBool(prevMore.telephony_omnichannel),
    transfer_people: prevMore.transfer_people ?? [],
    transfer_departament: prevMore.transfer_departament ?? '',
    sip_enabled: toBool(prevMore.sip_enabled),
    sip_user: prevMore.sip_user ?? '',
    sip_password: prevMore.sip_password ?? '',
    ignore_messages: toBool(prevMore.ignore_messages),
    receive_groups: toBool(prevMore.receive_groups),
    whatsapp_business_verified: toBool(prevMore.whatsapp_business_verified),
}

// O Toggle trabalha com string ('true' | ''), mas o more deve ser salvo como booleano
function moreToggle(key) {
    return computed({
        get: () => (store.channel.more[key] ? 'true' : ''),
        set: (value) => {
            store.channel.more[key] = value === 'true'
        },
    })
}

const rejectCalls = moreToggle('reject_calls')
const ignoreMessages = moreToggle('ignore_messages')
const receiveGroups = moreToggle('receive_groups')
const fileName = moreToggle('file_name')
const telephonyOmnichannel = moreToggle('telephony_omnichannel')
const sipEnabled = moreToggle('sip_enabled')

const savedCod = prevMore.country_cod != null && String(prevMore.country_cod).trim() !== ''
    ? String(prevMore.country_cod).replace(/\D/g, '')
    : ''
if (!store.channel.country_cod || String(store.channel.country_cod).trim() === '') {
    store.channel.country_cod = savedCod || '55'
}

if (store.channel?.start_service) {
    authorizationAttendance.value = 'true'
}

function getPhone(evt) {
    if (evt.cod !== undefined) {
        store.channel.country_cod = evt.cod
        store.channel.user = ''
        return
    }
    if (evt.phone !== undefined) {
        store.channel.user = evt.phone
    }
}

function handleToggleAuthorization(value) {
    authorizationAttendance.value = value
    store.channel.start_service = null
}

const whatsappBusinessVerified = computed({
    get() {
        return toBool(store.channel?.more?.whatsapp_business_verified)
    },
    set(value) {
        store.channel.more.whatsapp_business_verified = value === true
    },
})

function handleWhatsappBusinessVerifiedLabelClick() {
    whatsappBusinessVerified.value = !whatsappBusinessVerified.value
}

const acceptTerms = ref(false)

function handleAcceptTerms(value) {
    acceptTerms.value = value
    emit('update:acceptTerms', value)
}

watch(() => store.channel.more.telephony_omnichannel, (val) => {
    if (val) {
        store.channel.more.sip_enabled = false
    }
})

watch(() => store.channel.more.sip_enabled, (val) => {
    if (val) {
        store.channel.more.telephony_omnichannel = false
    }
})
</script>

<template>
    <template v-if="store.formStep === 2">
        <div class="space-y-4">
            <FeedbackAlert type="info" title="Fique atento!"
                content="Cuidado para não inserir um número e escanear o QRCode com o aplicativo de número divergente. Isso, poderá causar confusão e transtornos durante o atendimento!" />

            <InputFormHorizontal v-model="store.channel.name" :init-value="store.channel.name"
                :block-edit="store.loading" label="Nome do canal"
                placeholder="Como você irá identificar o canal pelo sistema?" rules="required" vertical />

            <SelectContactCountryCode position-label="top" label="Número do Whatsapp" :value-phone="store.channel.user"
                :country-cod="store.channel.country_cod" :block-edit="store.loading" class="px-0"
                @phone-value="getPhone" />

            <TabView>
                <TabPanel header="Gerais">
                    <div class="space-y-4 pt-2">
                        <Toggle v-model="authorizationAttendance" t-label="Autorização de inicio de atendimento"
                            t-description="Ao habilitar essa função é possivel relacionar um usuario ou um departamento para iniciar um atendimento"
                            @update:model-value="handleToggleAuthorization" />
                        <ChatTransition :show="authorizationAttendance === 'true'" custom-class="form_group">
                            <TabUserDepartment v-model="store.channel.start_service" />
                        </ChatTransition>

                        <Toggle v-model="fileName" t-label="Enviar arquivos com o nome real?"
                            t-description="Por padrão, os arquivos são enviados com nomes criptografados. Ativando essa opção, o nome do arquivo será o nome real!" />

                        <Toggle v-model="rejectCalls" t-label="Rejeitar automaticamente ligações?"
                            t-description="Caso deseje, podemos rejeitar automaticamente as ligações recebidas! Basta ativar a opção!" />

                        <Toggle
                            v-model="ignoreMessages" t-label="Ignorar todas as mensagens?"
                            t-description="Ativando essa opção, o sistema irá ignorar todas as mensagens recebidas!" />

                        <Toggle
                            v-if="signatureStore().existFeature('groups')"
                            v-model="receiveGroups"
                            t-label="Receber mensagens de grupos?"
                            t-description="Ativando essa opção, o canal também receberá mensagens enviadas em grupos." />

                        <div class="flex items-start gap-3 p-4 border border-slate-200 bg-white rounded-lg dark:border-slate-700 dark:bg-slate-800/40">
                            <Checkbox
                                input-id="whatsapp-business-verified"
                                v-model="whatsappBusinessVerified"
                                :binary="true"
                                :disabled="store.loading"
                                class="mt-0.5 shrink-0"
                            />
                            <label
                                for="whatsapp-business-verified"
                                class="cursor-pointer"
                                @click.prevent="handleWhatsappBusinessVerifiedLabelClick">
                                <p class="text-sm font-medium text-slate-700 dark:text-slate-100">
                                    Canal possui selo verificado do WhatsApp ?
                                </p>
                                <p class="text-xs text-slate-500 mt-1 dark:text-slate-300">
                                    Distintivo azul exibido ao lado do nome da empresa no WhatsApp. Marque apenas se o número já possui essa verificação oficial.
                                </p>
                            </label>
                        </div>

                    </div>
                </TabPanel>

                <TabPanel v-if="signatureStore().existFeature('telephony_omnichannel')" header="Telefonia">
                    <div class="space-y-4 pt-2">
                        <div class="border border-gray-200 dark:border-neutral-600 rounded-lg p-4 space-y-3">
                            <Toggle v-model="telephonyOmnichannel"
                                t-label="Habilitar ligações via telefonia omnichannel?"
                                t-description="Ao habilitar, a telefonia omnichannel ficará disponível na tela de atendimento." />

                            <template v-if="store.channel.more.telephony_omnichannel">
                                <TelephonyOmniChannelUsersDepartaments
                                    v-model:people="store.channel.more.transfer_people"
                                    v-model:departament="store.channel.more.transfer_departament" />
                            </template>
                        </div>

                        <div class="border border-gray-200 dark:border-neutral-600 rounded-lg p-4 space-y-3">
                            <Toggle v-model="sipEnabled"
                                t-label="Habilitar SIP?"
                                t-description="Ao habilitar, será disponibilizado o SIP para o canal. Caso habilitado, a telefonia omnichannel será desabilitada." />

                            <template v-if="store.channel.more.sip_enabled">

                                <InputFormHorizontal
                                    label="Server"
                                    :init-value="'server' + store.channel.server_id + '.chatmix.com.br'"
                                    :block-edit="true"
                                    description="Server padrão para o SIP. Esse valor não pode ser alterado."
                                    disabled
                                    vertical />

                                <InputFormHorizontal
                                    label="Porta"
                                    :init-value="5060"
                                    :block-edit="true"
                                    description="Porta padrão para o SIP. Esse valor não pode ser alterado."
                                    disabled
                                    vertical />

                                <InputFormHorizontal v-model="store.channel.more.sip_user"
                                    label="Usuário"
                                    :init-value="store.channel.more.sip_user"
                                    description="Usuário para o SIP. Você usará esse usuário para registrar o telefone no servidor SIP."
                                    vertical
                                    placeholder="Usuário para o SIP." />

                                <InputFormHorizontal v-model="store.channel.more.sip_password"
                                    label="Senha"
                                    type="password"
                                    :init-value="store.channel.more.sip_password"
                                    description="Senha para o SIP. Você usará essa senha para registrar o telefone no servidor SIP."
                                    vertical
                                    placeholder="Senha para o SIP." />

                            </template>
                        </div>
                    </div>
                </TabPanel>
            </TabView>

            <AlertPrice />
        </div>
    </template>

    <template v-else-if="store.formStep === 1">
        <div class="flex flex-col gap-4 dark:text-gray-400">
            <span class="text-lg font-bold mt-4">
                Instruções de cadastro
            </span>

            <div class="w-full">
                <div class="w-full text-start">
                    Ao conectar sua conta WhatsApp via QR Code, você assume total responsabilidade pelo uso desta
                    funcionalidade e reconhece que ela pode contrariar as Políticas de Uso da Meta e poderá causar
                    eventuais penalidades, suspensões ou bloqueios decorrentes dessa ação.
                </div>
                <div class="w-full flex border-t mt-3 border-gray-200">

                    <div class="flex align-items-center py-4">
                        <Checkbox v-model="acceptTerms" inputId="termos" name="termos" :value="true"
                            @update:model-value="handleAcceptTerms" />
                        <label for="termos" class="ml-2 -mt-1"> Concordo com os termos e desejo continuar </label>
                    </div>
                </div>
            </div>
        </div>


    </template>
</template>

<style lang="postcss" scoped>
.form_group {
    @apply w-full mb-5 bg-gray-100 dark:bg-neutral-500 dark:bg-opacity-30 p-4 space-y-3 rounded-md;
}
</style>
